/**
 * created by mleitzell on 8/5/16.
 *
 * if all columns in a grid are assigned a width, the width of the column becomes percentage-based
 * because kendo wants the columns to fill the space.
 * any columns without a width are given a percentage-based width to fill the space.
 * so we are making sure all columns are saved with a width except the last column (spacer) which will fill the rest of the space
 */
import functions from "common/helper/functions";
import arrayUtil from "common/helper/arrayUtil";
import workspaceOperations from "common/datasources/windowSettings/workspaceOperations";
import EventNamespaceMixin from "common/widgets/base/EventNamespaceMixin";

export default {
    ...EventNamespaceMixin,

    // options:
    // - autoSaveColumns string The id of the window group
    // - fluid boolean whether to turn on fluid layout (spacer) or not, this is always on if autoSaveColumns is specified

    /**
     * Appends the spacer column to the end of the columns array
     * This must be called before base class init
     * @param options object The options for the widget
     * @private
     */
    _fluidColumn: function(options) {
        if (options.autoSaveColumns) {
            // autosave requires fluid
            options.fluid = true;
        }

        if (options.fluid === true && options.columns) {
            // append spacer column at end for flex
            options.columns.push(this._getSpacerColumnDefinition());
        }
    },

    /**
     * Handle the autoSaveColumns feature.
     * @private
     */
    _autoSaveColumns: function() {
        this.unbindNamespace("autosave");
        if (this.options.autoSaveColumns) {
            // load saved window settings
            this._loadColumnSettings();
            this.bindWithNamespace("autosave", "columnShow", $.proxy(this._loadColumnSettings, this));
            this.bindWithNamespace("autosave", "columnHide", $.proxy(this._loadColumnSettings, this));

            // look at reorderable and resizable options to figure out what events to listen for
            if(this.options.resizable) {
                this.bindWithNamespace("autosave", "columnResize", functions.debounce($.proxy(this._saveGridColumnsSizeOnly, this), 1000));
            }

            if(this.options.reorderable) {
                this.bindWithNamespace("autosave", "columnReorder", functions.debounce($.proxy(this._saveGridColumns, this), 1000));
            }
        }
        // make spacer column fluid (auto resizes to fit empty space) for when user resizes a data column
        if (this.options.fluid === true && this.options.columns) {
            this.element.addClass("fluidGrid");
        }
    },

    _doSaveGridColumns: function(doSavePosition) {
        // always save all columns.
        let autoSaveColumns = this.columns.map(function(column, idx) {
            let ret = {
                gridName: this.options.autoSaveColumns,
                columnName: column.field,
                columnAlias: column.alias && column.alias.length <= 255 ? column.alias : undefined
            };

            if (this.options.resizable) {
                ret.width = this.getColumnWidth(column);

                // if width is 0, don't save it.
                if (ret.width === 0) {
                    delete ret.width;
                }
            }

            if(this.options.reorderable && (doSavePosition || this.hasSavedPositions)) {
                ret.position = idx;
            } else {
                // server requires it, set to 0
                ret.position = 0;
            }

            return ret;
        }, this);

        workspaceOperations.saveGridColumns(this.options.autoSaveColumns, autoSaveColumns);
    },

    _saveGridColumnsSizeOnly: function() {
        this._doSaveGridColumns(false);
    },

    /**
     * Save the grid column order and widths as window settings.
     * @private
     */
    _saveGridColumns: function() {
        this.hasSavedPositions = true;
        this._doSaveGridColumns(true);
    },

    /**
     * Load column settings.
     *
     * @protected
     */
    _loadColumnSettings: function() {
        if (this.options.autoSaveColumns) {
            workspaceOperations.getGridColumns(this.options.autoSaveColumns).done($.proxy(function(columns) {
                if (columns && columns.length) {
                    // load saved orders for this grid
                    this._applyColumnSettings(columns);
                }
            }, this));
        }
    },

    hasSavedPositions: false,

    /**
     * Apply column settings.
     *
     * @param settings array of window settings where columnName == column.field.
     * @private
     */
    _applyColumnSettings: function(settings) {

        //normalize setting positions, so that even if position is not provided it can be inferred from array order
        settings = arrayUtil
            .stableSort(settings, function(s1, s2) {
                return s1.position - s2.position;
            });
        const NonZeroValue = settings.filter(setting => setting.position > 0);
        this.hasSavedPositions = NonZeroValue.length > 0;
        settings.forEach(function(setting, i) {
            if (setting.position === undefined || setting.position === null) {
                setting.position = settings.length + i;
            }
        });

        //Generate a map of column fields-> settings
        //This avoids constant looping
        var colSettingByField = {};
        var colSettingByAlias = {};
        settings.forEach(function(setting, i) {
            colSettingByField[setting.columnName] = setting;
            if (setting.columnAlias) {
                colSettingByAlias[setting.columnAlias] = setting;
            }
        });

        var getSettingByColumn = function(column, value) {
            if (column.alias && colSettingByAlias.hasOwnProperty(column.alias)) {
                return colSettingByAlias[column.alias][value];
            } else if (colSettingByField.hasOwnProperty(column.field) && !colSettingByField[column.field].columnAlias) {
                return colSettingByField[column.field][value];
            } else {
                return null;
            }
        };

        if (this.options.reorderable) {
            //1. User sorted columns go to the left
            //2. Admin-only sorted columns(this.columns) go to the right
            //3. Trust relative sorting in each of those collections
            var cols = arrayUtil.stableSort(this.columns, function(col1, col2) {
                var pos1 = getSettingByColumn(col1, "position");
                var pos2 = getSettingByColumn(col2, "position");
                if (col1.reorderable === false || col2.reorderable === false) {
                    //If either column is not reorderable, it must stay in its current position
                    //This will only work if starts at the beginning or end
                    return 0;
                } else if (pos1 === pos2) {
                    //If both are null or both are 0(in case of saved widths only), this.columns has the correct order. Stable sort can handle that
                    return 0;
                } else if (pos1 === null) {
                    //if only pos1 is null, the user has sorted pos2
                    return 1;
                } else if (pos2 === null) {
                    //if only pos2 is null, the user has sorted pos1
                    return -1;
                } else {
                    //if neither are null, sort using user values
                    return pos1 - pos2;
                }
            });

            cols.forEach(function(column, i) {
                if (this.columns[i] !== column) {
                    this.reorderColumn(i, column);
                }
            }, this);
        }

        // resize columns, DOES NOT HANDLE locked columns
        if (this.options.resizable) {
            this.columns.forEach(function(col) {
                var width = getSettingByColumn(col, "width");
                if (width !== null && width !== undefined) {
                    this.setColumnWidth(col, width);
                }
            }, this);
        }
    },

    /**
     * Get column width.
     *
     * @param column column
     * @return {number} width in pixels
     */
    getColumnWidth: function(column) {
        if (column.hasOwnProperty("width") || !this.thead) {
            return column.width;
        } else {
            return this.thead.find("th[data-field='" + column.field + "']").outerWidth();
        }
    },

    /**
     * Set column width.
     *
     * @param column column
     * @param width width
     */
    setColumnWidth: function(column, width) {
        if (!column.hidden && width > 0 && column.autoSave !== false) {
            // width is a number since it is compared to a number above
            width = Number(width);
            column.width = width;
            var tableIndex = this._getColumnIndexInTable(column);
            if (this.thead) {
                this.thead.closest("table").find("colgroup col:not(.k-group-col)").eq(tableIndex).width(width);
            }
            if (this.tbody) {
                this.tbody.closest("table").find("colgroup col:not(.k-group-col)").eq(tableIndex).width(width);
            }
        }
    },

    /**
     * Get column's index in table.
     *
     * @param column column
     * @return {number} index (0-based)
     * @private
     */
    _getColumnIndexInTable: function(column) {

        // index in columns
        var index = this.columns.indexOf(column);

        // determine how many hidden columns come before column
        var hiddenColumns = this.columns.filter(function(column, i) {
            return (i < index && column.hidden === true);
        });

        return (index - hiddenColumns.length);
    },

    /**
     * Get spacer column definition.
     *
     * @return {*} column definition
     * @protected
     */
    _getSpacerColumnDefinition: function() {
        return {
            field: "spacer",
            title: " ",
            phone: false,
            template: kendo.template(''),
            autoSave: false,
            sortable: false,
            filterable: false,
            reorderable: false,
            resizable: false,
            menu: false
        };
    }
};
